import {
  LBButton,
  LBStarIcon,
  LBTooltip,
  LBTooltipContent,
  LBTooltipTrigger
} from '@linearb/buzz'
import { type MouseEvent } from 'react'

import useFavoriteOrgs from '../hooks/useFavoriteOrgs'
import { trackAction } from '../lib/datadogRum'
import type { FavoriteOrgId } from '../utils/favoriteOrgsStorage'

type Size = 'sm' | 'md'

const SIZE_CLASSES: Record<Size, { button: string; icon: string }> = {
  sm: { button: 'h-7 w-7', icon: 'h-4 w-4' },
  md: { button: 'h-8 w-8', icon: 'h-5 w-5' }
}

interface FavoriteOrgButtonProps {
  orgId: FavoriteOrgId
  size?: Size
  stopPropagation?: boolean
}

const FavoriteOrgButton = ({
  orgId,
  size = 'md',
  stopPropagation = false
}: FavoriteOrgButtonProps) => {
  const { isFavorite, toggleFavorite } = useFavoriteOrgs()
  const favorited = isFavorite(orgId)
  const label = favorited ? 'Remove from favorites' : 'Add to favorites'
  const { button: buttonSize, icon: iconSize } = SIZE_CLASSES[size]

  const handleClick = (e: MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation()
    }
    trackAction('favorite_toggled', { orgId, favorited: !favorited })
    toggleFavorite(orgId)
  }

  return (
    <LBTooltip>
      <LBTooltipTrigger asChild>
        <LBButton
          variant='ghost'
          size='sm'
          onClick={handleClick}
          className={`${buttonSize} p-0 shrink-0 hover:text-yellow-500 ${
            favorited ? 'text-yellow-500' : 'text-muted-foreground'
          }`}
          aria-label={label}
          aria-pressed={favorited}
        >
          <LBStarIcon
            className={iconSize}
            fill={favorited ? 'currentColor' : 'none'}
          />
        </LBButton>
      </LBTooltipTrigger>
      <LBTooltipContent>{label}</LBTooltipContent>
    </LBTooltip>
  )
}

export default FavoriteOrgButton
